import { Link, NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { 
  IconUser, 
  IconSettings, 
  IconLogout, 
  IconBuilding, 
  IconSearch, 
  IconCalendarPlus, 
  IconCalendar, 
  IconMenu2, 
  IconChevronLeft, 
  IconChevronRight, 
  IconTrophy, 
  IconAward,
  IconInfoCircle,
  IconX,
  IconCards
} from '@tabler/icons-react';
import styles from './MainLayout.module.css';
import { ROUTES } from '../constants/routes';
import { useAuth } from '../auth/hooks/useAuth';
import { userService } from '../services/api';
import { rewardsService, UserPoints } from '../services/rewardsApi';
import UserIdIndicator from './UserIdIndicator';
import { SidebarProvider, useSidebar } from '../contexts/SidebarContext';

interface MainLayoutProps {
  children?: React.ReactNode;
}

// Fonctions pour masquer les données sensibles dans les logs
const maskEmail = (email: string | null): string => {
  if (!email) return 'email-inconnu';
  const [username, domain] = email.split('@');
  if (!username || !domain) return 'email-invalide';
  return `${username.substring(0, 2)}***@${domain}`;
};

const maskUid = (uid: string | undefined): string => {
  if (!uid || uid.length < 8) return 'uid-masqué';
  return `${uid.substring(0, 4)}...${uid.substring(uid.length - 4)}`;
};

export function MainLayout({ children }: MainLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useSidebar();
  const sidebarRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { user, isAuthenticated: isLoggedIn, logout, isLoading: loading } = useAuth();
  const [userRole, setUserRole] = useState<string | undefined>(user?.role);
  const [userPoints, setUserPoints] = useState<UserPoints | null>(null);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [showUserId, setShowUserId] = useState(false);
  const userHoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showRewardsInfo, setShowRewardsInfo] = useState(false);

  // Vérifier si l'écran est de taille mobile lors du chargement et lors des redimensionnements
  useEffect(() => {
    // Fonction pour vérifier la taille de l'écran
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Vérifier au chargement initial
    checkIfMobile();
    
    // Ajouter un écouteur pour le redimensionnement de fenêtre
    window.addEventListener('resize', checkIfMobile);
    
    // Nettoyer l'écouteur
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Effet pour détecter les clics à l'extérieur de la sidebar en mode mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Vérifier si on est en mode mobile (largeur d'écran < 768px)
      const isMobile = window.innerWidth <= 768;
      
      // Si on est en mode mobile, la sidebar est ouverte, et le clic est en dehors de la sidebar
      if (
        isMobile && 
        sidebarOpen && 
        sidebarRef.current && 
        !sidebarRef.current.contains(event.target as Node) &&
        menuButtonRef.current && 
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
    };

    // Ajouter l'écouteur d'événements uniquement si la sidebar est ouverte
    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen, setSidebarOpen]);

  // Effet pour forcer une mise à jour des données utilisateur depuis l'API au chargement
  useEffect(() => {
    // Variable pour éviter les appels redondants au sein de la même session
    if (window.__userDataRefreshed) {
      return;
    }
    
    const refreshUserData = async () => {
      if (isLoggedIn && user) {
        try {
          // Forcer la suppression du cache pour récupérer des données fraîches
          const freshUserData = await userService.getCurrentUser();
          
          // Afficher toutes les propriétés pour le débogage
          console.log('Données utilisateur complètes (structure):', Object.keys(freshUserData));
          // Créer une version masquée pour les logs
          const maskedUserData = {
            ...freshUserData,
            email: maskEmail(freshUserData.email),
            firebaseUid: maskUid(freshUserData.firebaseUid)
          };
          console.log('Données utilisateur rafraîchies:', maskedUserData);
          console.log('Rôle utilisateur rafraîchi:', freshUserData.role);
          console.log('Type utilisateur (userType):', freshUserData.userType);
          console.log('Type profile (profileType):', freshUserData.profileType);
          
          // Utiliser le rôle, userType ou profileType selon ce qui est disponible
          const userRoleValue = freshUserData.role || freshUserData.userType || freshUserData.profileType;
          console.log('Valeur effective du rôle utilisée:', userRoleValue);
          
          setUserRole(userRoleValue);
          
          // Marquer comme rafraîchi pour éviter les appels redondants
          window.__userDataRefreshed = true;
          
          // Renouveler le marqueur après 5 minutes pour permettre un rafraîchissement périodique
          setTimeout(() => {
            window.__userDataRefreshed = false;
          }, 5 * 60 * 1000);
        } catch (error) {
          console.error('Erreur lors du rafraîchissement des données utilisateur:', error);
        }
      }
    };
    
    refreshUserData();
  }, [isLoggedIn, user]);

  // Effet pour charger les points et le niveau de l'utilisateur
  useEffect(() => {
    // Variable pour éviter les appels redondants au sein de la même session
    if (window.__userPointsRefreshed) {
      return;
    }
    
    const fetchUserPoints = async () => {
      if (isLoggedIn && user) {
        setLoadingPoints(true);
        try {
          // Mettre à jour la série de connexions (streak) - une seule fois par session
          try {
            await rewardsService.updateLoginStreak();
          } catch (error) {
            console.warn('Erreur lors de la mise à jour du streak (fonctionnalité ignorée):', error);
          }
          
          // Récupérer les points de l'utilisateur
          try {
            const points = await rewardsService.getUserPoints();
            setUserPoints(points);
          } catch (error) {
            console.error('Erreur lors de la récupération des points:', error);
          }
          
          // Vérifier les badges - étape facultative
          try {
            await rewardsService.checkBadges();
          } catch (error) {
            console.warn('Erreur lors de la vérification des badges (fonctionnalité ignorée):', error);
          }
          
          // Marquer comme rafraîchi pour éviter les appels redondants
          window.__userPointsRefreshed = true;
          
          // Renouveler le marqueur après 5 minutes pour permettre un rafraîchissement périodique
          setTimeout(() => {
            window.__userPointsRefreshed = false;
          }, 5 * 60 * 1000);
        } catch (error) {
          console.error('Erreur générale lors du chargement des récompenses:', error);
        } finally {
          setLoadingPoints(false);
        }
      }
    };
    
    fetchUserPoints();
  }, [isLoggedIn, user]);

  // Vérifier si l'utilisateur a les droits d'administration (uniquement admin)
  const hasAdminRights = 
    (userRole === 'admin') ||
    (user?.role === 'admin') ||
    ((user as any)?.userType === 'admin' || (user as any)?.userType === 'org_admin') ||
    ((user as any)?.profileType === 'admin' || (user as any)?.profileType === 'org_admin');

  // Logger les informations détaillées sur l'utilisateur connecté
  useEffect(() => {
    if (user && isLoggedIn) {
      console.log('🔍 DÉTAILS UTILISATEUR CONNECTÉ:');
      console.log(`Email: ${maskEmail(user.email)}`);
      console.log(`UserID: ${user.userId || 'Non défini'}`);
      console.log(`Rôle (user.role): ${user.role || 'Non défini'}`);
      console.log(`UserType: ${(user as any)?.userType || 'Non défini'}`);
      console.log(`ProfileType: ${(user as any)?.profileType || 'Non défini'}`);
      console.log(`FirebaseUID: ${maskUid(user.firebaseUid)}`);
      console.log(`UserRole variable: ${userRole}`);
      console.log(`Admin rights: ${hasAdminRights ? 'OUI' : 'NON'}`);
      
      // Afficher l'objet user complet (masqué pour la sécurité)
      const safeUser = {...user};
      if (safeUser.email) safeUser.email = maskEmail(safeUser.email);
      if (safeUser.firebaseUid) safeUser.firebaseUid = maskUid(safeUser.firebaseUid);
      console.log('Données utilisateur complètes:', safeUser);
    }
  }, [user, isLoggedIn, userRole, hasAdminRights]);

  // Note: displayName conservé dans le code mais non utilisé dans l'interface
  /* const displayName = user 
    ? (user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user.name || 'Utilisateur')
    : 'Utilisateur'; */

  // Rediriger vers la page de connexion si l'utilisateur n'est pas connecté
  useEffect(() => {
    if (!loading && !isLoggedIn) {
      navigate(ROUTES.LOGIN);
    }
  }, [isLoggedIn, navigate, loading]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleUserIconMouseEnter = () => {
    // Définir un timer pour afficher l'ID utilisateur après 4 secondes
    userHoverTimerRef.current = setTimeout(() => {
      setShowUserId(true);
    }, 4000); // 4 secondes
  };

  const handleUserIconMouseLeave = () => {
    // Annuler le timer si l'utilisateur quitte l'icône avant les 4 secondes
    if (userHoverTimerRef.current) {
      clearTimeout(userHoverTimerRef.current);
      userHoverTimerRef.current = null;
    }
    // Masquer l'ID utilisateur
    setShowUserId(false);
  };

  // Afficher un indicateur de chargement pendant la vérification de l'authentification
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className={styles.layout}>
        <aside 
          className={`${styles.sidebar} ${isMobile ? (sidebarOpen ? styles.open : '') : (sidebarOpen ? styles.open : styles.closed)}`} 
          data-testid="sidebar"
          ref={sidebarRef}>
          <div className={styles.sidebarHeader}>
            <Link to="/" className={styles.logo}>
              <img src="/logo_etudown_title.png" alt="Etudown" style={{ maxHeight: 40, width: 'auto', display: 'block' }} />
            </Link>
            <button 
              className={styles.sidebarToggle} 
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? "Réduire le menu" : "Agrandir le menu"}
              title={sidebarOpen ? "Réduire le menu" : "Agrandir le menu"}
            >
              {sidebarOpen ? <IconChevronLeft size={20} /> : <IconChevronRight size={20} />}
            </button>
          </div>
          
          <nav className={styles.sidebarNav}>
            <NavLink 
              to="/events" 
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
              title="Événements"
              onClick={() => isMobile && setSidebarOpen(false)}
            >
              <IconSearch size={20} />
              <span className={styles.navLinkText}>Événements</span>
            </NavLink>
            
            {/* Ajout du lien vers la page de swipe dans la sidebar */}
            <NavLink 
              to={ROUTES.SWIPE_EVENTS} 
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
              title="Swiper les événements"
              onClick={() => isMobile && setSidebarOpen(false)}
            >
              <IconCards size={20} />
              <span className={styles.navLinkText}>Swiper</span>
            </NavLink>
            
            {hasAdminRights && (
              <NavLink 
                to="/organization" 
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                title="Organisation"
                onClick={() => isMobile && setSidebarOpen(false)}
              >
                <IconBuilding size={20} />
                <span className={styles.navLinkText}>Organisation</span>
              </NavLink>
            )}
            
            {hasAdminRights && (
              <NavLink 
                to="/events/new" 
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                title="Créer un événement"
                onClick={() => isMobile && setSidebarOpen(false)}
              >
                <IconCalendarPlus size={20} />
                <span className={styles.navLinkText}>Créer un événement</span>
              </NavLink>
            )}
            
            <NavLink 
              to={ROUTES.CALENDAR} 
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
              title="Calendrier"
              onClick={() => isMobile && setSidebarOpen(false)}
            >
              <IconCalendar size={20} />
              <span className={styles.navLinkText}>Calendrier</span>
            </NavLink>
            
            {/* Lien vers la page d'administration pour les utilisateurs admin */}
            {hasAdminRights && (
              <NavLink 
                to={ROUTES.ADMIN} 
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                title="Administration"
                onClick={() => isMobile && setSidebarOpen(false)}
              >
                <IconSettings size={20} />
                <span className={styles.navLinkText}>Administration</span>
              </NavLink>
            )}
            
            <div className={styles.navDivider}></div>
            
            <NavLink 
              to={ROUTES.USER_PROFILE} 
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
              title="Mon profil"
              onClick={() => isMobile && setSidebarOpen(false)}
              onMouseEnter={handleUserIconMouseEnter}
              onMouseLeave={handleUserIconMouseLeave}
            >
              <IconUser size={20} />
              <span className={styles.navLinkText}>Mon profil</span>
            </NavLink>
          </nav>
          
          {/* Encart de points et progression */}
          {isLoggedIn && (
            <div className={styles.rewardsPanel}>
              <div className={styles.rewardsPanelHeader}>
                <IconTrophy size={20} />
                <span className={styles.rewardsPanelTitle}>Mes récompenses</span>
                <button 
                  className={styles.infoButton}
                  onClick={() => setShowRewardsInfo(!showRewardsInfo)}
                  aria-label="Informations sur les récompenses"
                  title="Informations sur les récompenses"
                >
                  <IconInfoCircle size={16} />
                </button>
              </div>
              
              {showRewardsInfo && (
                <div className={styles.rewardsInfoTooltip}>
                  <div className={styles.tooltipHeader}>
                    <h4>Système de récompenses</h4>
                    <button 
                      className={styles.closeTooltip}
                      onClick={() => setShowRewardsInfo(false)}
                      aria-label="Fermer"
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                  <div className={styles.tooltipContent}>
                    <p>Gagnez des points en participant activement :</p>
                    <ul>
                      <li>+5 points pour chaque connexion quotidienne</li>
                      <li>+10 points quand vous participez à un événement</li>
                      <li>+15 points quand vous organisez un événement</li>
                      <li>+20 points pour une série de 7 jours de connexion</li>
                    </ul>
                    <p>Les niveaux débloquent de nouvelles fonctionnalités et badges exclusifs !</p>
                  </div>
                </div>
              )}
              
              {loadingPoints ? (
                <div className={styles.rewardsPanelLoading}>Chargement...</div>
              ) : userPoints ? (
                <>
                  <div className={styles.pointsContainer}>
                    <div className={styles.pointsValue}>
                      <span>{userPoints.total}</span> points
                    </div>
                    <div className={styles.levelBadge}>
                      <IconAward size={16} />
                      <span>Niveau {userPoints.level}</span>
                    </div>
                  </div>
                  
                  <div className={styles.progressContainer}>
                    <div className={styles.progressLabel}>
                      <span>Niveau {userPoints.nextLevel}</span>
                      {userPoints.nextLevelThreshold && (
                        <span>{userPoints.total} / {userPoints.nextLevelThreshold}</span>
                      )}
                    </div>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill}
                        style={{ width: `${userPoints.progress * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </>
              ) : (
                // Afficher un état par défaut si les données de points ne sont pas disponibles
                <>
                  <div className={styles.pointsContainer}>
                    <div className={styles.pointsValue}>
                      <span>0</span> points
                    </div>
                    <div className={styles.levelBadge}>
                      <IconAward size={16} />
                      <span>Niveau 1</span>
                    </div>
                  </div>
                  
                  <div className={styles.progressContainer}>
                    <div className={styles.progressLabel}>
                      <span>Niveau 2</span>
                      <span>0 / 100</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill}
                        style={{ width: '0%' }}
                      ></div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </aside>
        
        <div className={styles.contentWrapper}>
          <header className={styles.header}>
            <div className={styles.headerContent}>
              {/* Bouton pour afficher le menu sur mobile */}
              <button 
                className={styles.mobileMenuToggle} 
                onClick={toggleSidebar}
                aria-label="Menu"
                ref={menuButtonRef}
              >
                <IconMenu2 size={24} />
              </button>
              
              {/* Titre EtuDown centré */}
              <div className={styles.mobileTitle}>
                <img src="/logo_etudown_title.png" alt="Etudown" style={{ maxHeight: 40, width: 'auto', display: 'block', margin: '0 auto' }} />
              </div>
              
              {/* Menu utilisateur toujours à droite */}
              <div className={styles.userMenu}>
                <button 
                  className={styles.userMenuButton} 
                  aria-haspopup="true" 
                  aria-expanded={menuOpen}
                  onClick={toggleMenu}
                  onMouseEnter={handleUserIconMouseEnter}
                  onMouseLeave={handleUserIconMouseLeave}
                >
                  <IconUser size={24} />
                </button>
                {menuOpen && (
                  <div 
                    className={styles.userMenuDropdown} 
                    role="menu"
                    onClick={() => setMenuOpen(false)} // Ferme le menu après un clic
                  >
                    <Link to={ROUTES.USER_PROFILE} className={styles.userMenuItem} role="menuitem">
                      <IconUser size={16} />
                      <span>Profil</span>
                    </Link>
                    {hasAdminRights && (
                      <Link to={ROUTES.ADMIN} className={styles.userMenuItem} role="menuitem">
                        <IconSettings size={16} />
                        <span>Administration</span>
                      </Link>
                    )}
                    <button 
                      className={styles.userMenuItem} 
                      role="menuitem" 
                      onClick={(e) => {
                        e.stopPropagation(); // Évite de déclencher le onClick du parent
                        handleLogout();
                      }}
                    >
                      <IconLogout size={16} />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>
          
          <main className={styles.main}>
            <div className={styles.container}>
              {children}
              <Outlet />
            </div>
          </main>
          
          <footer className={styles.footer}>
            <div className={styles.footerContent}>
              <p>&copy; 2025 EtuDown. Tous droits réservés.</p>
            </div>
          </footer>
        </div>

        {/* Navigation mobile (bottom navigation) - seulement affichée sur mobile */}
        {isMobile && (
          <nav className={styles.bottomNav}>
            <NavLink 
              to="/events" 
              className={({ isActive }) => `${styles.bottomNavLink} ${isActive ? styles.active : ''}`}
              title="Événements"
            >
              <IconSearch size={24} />
              <span className={styles.bottomNavText}>Événements</span>
            </NavLink>
            
            <NavLink 
              to={ROUTES.SWIPE_EVENTS} 
              className={({ isActive }) => `${styles.bottomNavLink} ${isActive ? styles.active : ''}`}
              title="Swiper"
            >
              <IconCards size={24} />
              <span className={styles.bottomNavText}>Swiper</span>
            </NavLink>
            
            <NavLink 
              to={ROUTES.CALENDAR} 
              className={({ isActive }) => `${styles.bottomNavLink} ${isActive ? styles.active : ''}`}
              title="Calendrier"
            >
              <IconCalendar size={24} />
              <span className={styles.bottomNavText}>Calendrier</span>
            </NavLink>
            
            {hasAdminRights && (
              <>
                <NavLink 
                  to="/organization" 
                  className={({ isActive }) => `${styles.bottomNavLink} ${isActive ? styles.active : ''}`}
                  title="Organisation"
                >
                  <IconBuilding size={24} />
                  <span className={styles.bottomNavText}>Organisation</span>
                </NavLink>

                <NavLink 
                  to="/events/new" 
                  className={({ isActive }) => `${styles.bottomNavLink} ${isActive ? styles.active : ''}`}
                  title="Créer"
                >
                  <IconCalendarPlus size={24} />
                  <span className={styles.bottomNavText}>Créer</span>
                </NavLink>
              </>
            )}
            
            <NavLink 
              to={ROUTES.USER_PROFILE} 
              className={({ isActive }) => `${styles.bottomNavLink} ${isActive ? styles.active : ''}`}
              title="Profil"
              onMouseEnter={handleUserIconMouseEnter}
              onMouseLeave={handleUserIconMouseLeave}
            >
              <IconUser size={24} />
              <span className={styles.bottomNavText}>Profil</span>
            </NavLink>
          </nav>
        )}

        {/* Indicateur d'ID utilisateur qui s'affiche uniquement après un survol de 4 secondes */}
        <UserIdIndicator isVisible={showUserId} />
      </div>
    </SidebarProvider>
  );
}
